import {
  Button,
  InfoDataTable,
  ModalComponentWrapper,
} from '@/shared/components';
import { useTranslation } from 'react-i18next';
import { formatReceivedAmount, useModalStore } from '@/shared/lib';
import { useExchangeResult, type ExchangeConfirmationArgs } from '../model';
import { useAssetsExchange } from '../api';
import { useAfterOperationRefresh } from '@/entities/operation';

const ExchangeConfirmationModal = ({
  title,
  amountToPay,
  amountToReceive,
  assetCodeToPay,
  assetCodeToReceive,
  conversionRate,
  formData,
  onSuccess,
}: ExchangeConfirmationArgs) => {
  const { t } = useTranslation();
  const { closeDialog } = useModalStore();

  const afterOperationRefresh = useAfterOperationRefresh();

  const { exchangeAssets, isExchangeAssetsLoading } = useAssetsExchange({
    onSuccess: () => {
      onSuccess?.();
      goToSuccessScreen();
      afterOperationRefresh();
    },
    onError: () => {
      goToErrorScreen();
    },
  });

  const data = [
    {
      key: t('exchange.youPay'),
      value: `${formatReceivedAmount(amountToPay)} ${assetCodeToPay}`,
    },
    {
      key: t('exchange.youReceive'),
      value: `≈ ${formatReceivedAmount(amountToReceive)} ${assetCodeToReceive}`,
    },
    {
      key: t('exchange.exchangeRate'),
      value: `${conversionRate.leftPart} ≈ ${conversionRate.rightPart}`,
    },
  ];

  const handleSubmit = async () => {
    await exchangeAssets(formData);
  };

  const { goToSuccessScreen, goToErrorScreen } = useExchangeResult();

  return (
    <ModalComponentWrapper title={title}>
      <InfoDataTable
        data={data}
        className="py-[1.6rem] px-[2.4rem] bg-secondary-bg rounded-[1.6rem]"
      />
      <div className="w-full flex gap-[1.6rem]">
        <Button
          variant="secondary"
          size="lg"
          onClick={closeDialog}
          className="flex-1">
          {t('common.cancel')}
        </Button>
        <Button
          variant="default"
          loading={isExchangeAssetsLoading}
          size="lg"
          onClick={handleSubmit}
          className="flex-1">
          {t('common.submit')}
        </Button>
      </div>
      <p className="text-center p4 text-secondary">
        {t('exchange.confirmationDescription')}
      </p>
    </ModalComponentWrapper>
  );
};

export { ExchangeConfirmationModal };
